import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, DatePicker, Select, Button, Typography, Spin, Alert, Modal, Form, Input, message, Popconfirm } from 'antd';
import { 
  BarChartOutlined, 
  UserOutlined, 
  ShoppingCartOutlined, 
  EyeOutlined,
  TrophyOutlined,
  WarningOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { theme } from '../../../styles/theme';
import { fetchAnalyticsData, fetchRealTimeAnalytics } from '../../../utils/firebaseAnalytics';
import { createCampaign, updateCampaign, deleteCampaign, generateCampaignUrl } from '../../../utils/firestoreMetrics';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

// Mock data for fallback
const mockAnalyticsData = {
  pageViews: 1250,
  uniqueUsers: 89,
  quotesSubmitted: 23,
  conversionRate: 25.8,
  topPages: [
    { page: '/', views: 450, key: 'home' },
    { page: '/QuotePage', views: 230, key: 'quote' },
    { page: '/services', views: 180, key: 'services' },
    { page: '/about', views: 120, key: 'about' },
    { page: '/contact', views: 90, key: 'contact' }
  ],
  recentEvents: [
    { event: 'quote_submitted', user: 'john@example.com', timestamp: '2024-01-15 14:30', value: '$1,200' },
    { event: 'page_view', user: 'anonymous', timestamp: '2024-01-15 14:25', value: '/QuotePage' },
    { event: 'service_selected', user: 'jane@example.com', timestamp: '2024-01-15 14:20', value: 'lawn_care' }
  ],
  errorEvents: [],
  campaignData: {
    'free-inspect-weed': {
      title: 'Free Inspection and Weed Pulling',
      visits: 150,
      quotes: 12,
      conversionRate: 8.0,
      revenue: 6000,
      campaign: 'free-inspect-weed'
    },
    '25-off-6-months': {
      title: '25% Off 6 month plan',
      visits: 200,
      quotes: 18,
      conversionRate: 9.0,
      revenue: 9000,
      campaign: '25-off-6-months'
    },
    '30-off-3-months': {
      title: '30% Off 3 month plan',
      visits: 120,
      quotes: 8,
      conversionRate: 6.7,
      revenue: 4000,
      campaign: '30-off-3-months'
    }
  }
};

const AnalyticsDashboard = () => {
  const [dateRange, setDateRange] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('all');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [campaignModalVisible, setCampaignModalVisible] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignForm] = Form.useForm();

  // Fetch analytics data
  const loadAnalyticsData = async (dateRange = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await fetchAnalyticsData(dateRange);
      if (result.success) {
        setAnalyticsData(result.data);
        setLastUpdated(new Date());
      } else {
        setError(result.error || 'Failed to load analytics data');
        // Use fallback data
        setAnalyticsData(mockAnalyticsData);
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError('Failed to load analytics data');
      setAnalyticsData(mockAnalyticsData);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadAnalyticsData(dateRange);
  }, [dateRange]);

  const handleDateChange = (dates) => {
    setDateRange(dates);
    loadAnalyticsData(dates);
  };

  const handleRefresh = () => {
    loadAnalyticsData(dateRange);
  };

  const handleMetricChange = (value) => {
    setSelectedMetric(value);
    // In production, filter data based on selected metric
    console.log('Metric changed:', value);
  };

  // Campaign management functions
  const handleCreateCampaign = () => {
    setEditingCampaign(null);
    campaignForm.resetFields();
    setCampaignModalVisible(true);
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    campaignForm.setFieldsValue({
      title: campaign.title,
      source: campaign.source,
      medium: campaign.medium,
      campaign: campaign.campaign,
      term: campaign.term,
      content: campaign.content
    });
    setCampaignModalVisible(true);
  };

  const handleDeleteCampaign = async (campaignKey) => {
    try {
      const result = await deleteCampaign(campaignKey);
      if (result.success) {
        message.success('Campaign deleted successfully');
        loadAnalyticsData(dateRange); // Refresh data
      } else {
        message.error(result.error || 'Failed to delete campaign');
      }
    } catch (error) {
      message.error('Error deleting campaign');
    }
  };

  const handleCampaignSubmit = async (values) => {
    try {
      let result;
      if (editingCampaign) {
        result = await updateCampaign(editingCampaign.id, values);
      } else {
        result = await createCampaign(values);
      }
      
      if (result.success) {
        message.success(editingCampaign ? 'Campaign updated successfully' : 'Campaign created successfully');
        setCampaignModalVisible(false);
        loadAnalyticsData(dateRange); // Refresh data
      } else {
        message.error(result.error || 'Failed to save campaign');
      }
    } catch (error) {
      message.error('Error saving campaign');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('URL copied to clipboard');
    }).catch(() => {
      message.error('Failed to copy URL');
    });
  };

  const columns = [
    {
      title: 'Quote ID',
      dataIndex: 'id',
      key: 'id',
      render: (text) => <Text code>{text.substring(0, 8)}...</Text>
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (text) => <Text ellipsis style={{ maxWidth: 150 }}>{text}</Text>
    },
    {
      title: 'Services',
      dataIndex: 'services',
      key: 'services',
      render: (services) => (
        <div>
          {services.slice(0, 2).map(service => (
            <Tag key={service} color="blue" style={{ marginBottom: 2 }}>
              {service.replace(/_/g, ' ')}
            </Tag>
          ))}
          {services.length > 2 && <Tag color="default">+{services.length - 2}</Tag>}
        </div>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (value) => <Text strong style={{ color: theme.colors.success }}>${value}</Text>
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date.toLocaleDateString()
    }
  ];

  const errorColumns = [
    {
      title: 'Error Type',
      dataIndex: 'error',
      key: 'error',
      render: (error) => (
        <Tag color="red" icon={<WarningOutlined />}>
          {error}
        </Tag>
      )
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      render: (count) => <Text strong>{count}</Text>
    },
    {
      title: 'Last Occurred',
      dataIndex: 'lastOccurred',
      key: 'lastOccurred'
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      render: (message) => (
        <Text style={{ fontSize: '12px', color: theme.colors.gray }}>
          {message ? (message.length > 50 ? `${message.substring(0, 50)}...` : message) : 'No message'}
        </Text>
      )
    }
  ];

  if (loading && !analyticsData) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const data = analyticsData || mockAnalyticsData;

  return (
    <div style={{ backgroundColor: 'transparent', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ 
          color: theme.colors.black, 
          fontFamily: theme.fonts.primary,
          marginBottom: '8px'
        }}>
          <BarChartOutlined /> Analytics Dashboard
        </h1>
        <p style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
          Monitor your application performance and user behavior
        </p>
        {error && (
          <Alert
            message="Data Loading Issue"
            description={error}
            type="warning"
            showIcon
            style={{ marginTop: '16px' }}
          />
        )}
        <div style={{ marginTop: '8px', color: theme.colors.gray, fontSize: '14px' }}>
          Last updated: {lastUpdated.toLocaleString()}
          {analyticsData && <span style={{ marginLeft: '16px', color: theme.colors.success }}>✓ Real-time data</span>}
          {!analyticsData && <span style={{ marginLeft: '16px', color: theme.colors.warning }}>⚠ Using fallback data</span>}
        </div>
      </div>

      {/* Controls */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16} align="middle">
          <Col>
            <RangePicker 
              onChange={handleDateChange}
              placeholder={['Start Date', 'End Date']}
            />
          </Col>
          <Col>
            <Select 
              value={selectedMetric} 
              onChange={handleMetricChange}
              style={{ width: 200 }}
            >
              <Option value="all">All Metrics</Option>
              <Option value="pageviews">Page Views</Option>
              <Option value="quotes">Quote Submissions</Option>
              <Option value="errors">Errors</Option>
            </Select>
          </Col>
          <Col>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh Data
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Page Views"
              value={data.totalPageViews || data.pageViews}
              prefix={<EyeOutlined />}
              valueStyle={{ color: theme.colors.primary }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Unique Users"
              value={data.uniqueUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: theme.colors.success }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Quotes Submitted"
              value={data.quotesSubmitted}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: theme.colors.warning }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Conversion Rate"
              value={data.conversionRate}
              suffix="%"
              prefix={<TrophyOutlined />}
              valueStyle={{ color: theme.colors.error }}
            />
          </Card>
        </Col>
      </Row>

      {/* Top Pages */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card title="Top Pages" style={{ height: '400px' }}>
            <Table
              dataSource={data.topPages || []}
              pagination={false}
              size="small"
              columns={[
                { title: 'Page', dataIndex: 'page', key: 'page' },
                { title: 'Views', dataIndex: 'views', key: 'views' },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Recent Quotes" style={{ height: '400px' }}>
            <Table
              dataSource={data.recentQuotes || []}
              pagination={false}
              size="small"
              columns={columns}
            />
          </Card>
        </Col>
      </Row>

      {/* Campaign Performance */}
      <Card 
        title="UTM Campaign Performance" 
        style={{ marginBottom: '24px' }}
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleCreateCampaign}
          >
            Add Campaign
          </Button>
        }
      >
        <Table
          dataSource={Object.entries(data.campaignData).map(([key, campaignData]) => ({
            key,
            id: key,
            ...campaignData
          }))}
          size="small"
          style={{ 
            fontSize: '13px'
          }}
          rowStyle={{
            height: 'auto',
            minHeight: '40px'
          }}
          scroll={{ x: 1000 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Campaign',
              dataIndex: 'title',
              key: 'title',
              width: 200,
              render: (text, record) => (
                <div style={{ lineHeight: '1.2' }}>
                  <div style={{ 
                    fontWeight: 'bold', 
                    fontSize: '12px',
                    marginBottom: '2px'
                  }}>
                    {text.length > 30 ? `${text.substring(0, 30)}...` : text}
                  </div>
                  <Text type="secondary" style={{ fontSize: '10px' }}>
                    {record.campaign}
                  </Text>
                </div>
              )
            },
            {
              title: 'Source',
              dataIndex: 'source',
              key: 'source',
              width: 100,
              render: (source) => <Tag color="blue" style={{ fontSize: '11px' }}>{source}</Tag>
            },
            {
              title: 'Medium',
              dataIndex: 'medium',
              key: 'medium',
              width: 120,
              render: (medium) => (
                <Tag 
                  color="green" 
                  style={{ 
                    fontSize: '11px',
                    whiteSpace: 'normal',
                    height: 'auto',
                    lineHeight: '1.2',
                    padding: '2px 6px',
                    maxWidth: '100%',
                    wordBreak: 'break-word'
                  }}
                >
                  {medium}
                </Tag>
              )
            },
            {
              title: 'Term',
              dataIndex: 'term',
              key: 'term',
              width: 80,
              render: (term) => term ? <Tag color="orange" style={{ fontSize: '11px' }}>{term}</Tag> : '-'
            },
            {
              title: 'Campaign URL',
              key: 'url',
              width: 180,
              render: (_, record) => {
                const url = generateCampaignUrl({
                  source: record.source,
                  medium: record.medium,
                  campaign: record.campaign,
                  term: record.term,
                  content: record.content
                });
                return (
                  <div style={{ maxWidth: '160px', lineHeight: '1.2' }}>
                    <Text 
                      code 
                      style={{ 
                        fontSize: '10px', 
                        wordBreak: 'break-all',
                        display: 'block',
                        marginBottom: '2px',
                        lineHeight: '1.1'
                      }}
                    >
                      {url.length > 50 ? `${url.substring(0, 50)}...` : url}
                    </Text>
                    <Button 
                      size="small" 
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(url)}
                      style={{ 
                        fontSize: '9px', 
                        height: '18px', 
                        padding: '0 6px',
                        lineHeight: '1'
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                );
              }
            },
            {
              title: 'Visits',
              dataIndex: 'visits',
              key: 'visits',
              width: 70,
              align: 'center',
              sorter: (a, b) => a.visits - b.visits,
            },
            {
              title: 'Quotes',
              dataIndex: 'quotes',
              key: 'quotes',
              width: 70,
              align: 'center',
              sorter: (a, b) => a.quotes - b.quotes,
            },
            {
              title: 'Conversion Rate',
              dataIndex: 'conversionRate',
              key: 'conversionRate',
              width: 100,
              align: 'center',
              render: (rate) => `${rate}%`,
              sorter: (a, b) => a.conversionRate - b.conversionRate,
            },
            {
              title: 'Revenue',
              dataIndex: 'revenue',
              key: 'revenue',
              width: 90,
              align: 'center',
              render: (amount) => `$${amount.toLocaleString()}`,
              sorter: (a, b) => a.revenue - b.revenue,
            },
            {
              title: 'Actions',
              key: 'actions',
              width: 120,
              render: (_, record) => (
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <Button 
                    size="small" 
                    icon={<EditOutlined />}
                    onClick={() => handleEditCampaign(record)}
                    style={{ 
                      height: '24px',
                      padding: '0 8px',
                      fontSize: '11px'
                    }}
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    title="Are you sure you want to delete this campaign?"
                    onConfirm={() => handleDeleteCampaign(record.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button 
                      size="small" 
                      danger 
                      icon={<DeleteOutlined />}
                      style={{ 
                        height: '24px',
                        padding: '0 8px',
                        fontSize: '11px'
                      }}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                </div>
              )
            }
          ]}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Error Tracking */}
      <Card title="Error Tracking" style={{ marginBottom: '24px' }}>
        {data.errorEvents && data.errorEvents.length > 0 ? (
          <Table
            dataSource={data.errorEvents}
            pagination={false}
            columns={errorColumns}
          />
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: theme.colors.gray,
            fontStyle: 'italic'
          }}>
            <WarningOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
            <div>No errors tracked in the selected time period</div>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              Errors will appear here when they occur in the application
            </div>
          </div>
        )}
      </Card>

      {/* Campaign Modal */}
      <Modal
        title={editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}
        open={campaignModalVisible}
        onCancel={() => setCampaignModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={campaignForm}
          layout="vertical"
          onFinish={handleCampaignSubmit}
        >
          <Form.Item
            name="title"
            label="Campaign Title"
            rules={[{ required: true, message: 'Please enter campaign title' }]}
          >
            <Input placeholder="e.g., Summer Lawn Care Special" />
          </Form.Item>

          <Form.Item
            name="source"
            label="UTM Source"
            rules={[{ required: true, message: 'Please enter UTM source' }]}
          >
            <Input placeholder="e.g., google, facebook, door-to-door" />
          </Form.Item>

          <Form.Item
            name="medium"
            label="UTM Medium"
            rules={[{ required: true, message: 'Please enter UTM medium' }]}
          >
            <Input placeholder="e.g., cpc, social, hangtag, email" />
          </Form.Item>

          <Form.Item
            name="campaign"
            label="UTM Campaign"
            rules={[{ required: true, message: 'Please enter UTM campaign' }]}
          >
            <Input placeholder="e.g., summer-2024-promo" />
          </Form.Item>

          <Form.Item
            name="term"
            label="UTM Term (Optional)"
          >
            <Input placeholder="e.g., lawn-care, landscaping" />
          </Form.Item>

          <Form.Item
            name="content"
            label="UTM Content (Optional)"
          >
            <Input placeholder="e.g., banner-ad, text-link" />
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button onClick={() => setCampaignModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AnalyticsDashboard;
